// composables/useAppHelpers.ts
import { customAlphabet } from "nanoid";
import { useAppStore } from "@/store/app.store";
import { useChatStore } from "@/store/chat.store";
import { ref } from "vue";
import { NavigationItem, Permissions, Role, RoleAction } from "@/enums/navigation";
import { useI18n } from "vue-i18n";

export function useBaseView() {
  const appStore = useAppStore();
  const chatStore = useChatStore();
  const { t } = useI18n();

  const avatarColor = (thread: any) => {
    if (thread.resolved || !thread.name || thread.archived) {
      return "#BDBDBD";
    }
    const colors = ["#9575CD", "#EF5350", "#7986CB", "#4FC3F7", "#81C784", "#FFD54F", "#FF8A65"];
    const sum = thread.name.split("").reduce((prev: number, char: string) => prev + char.charCodeAt(0), 0);
    return colors[sum % colors.length];
  };

  const roles = [
    { id: "ad", name: t("dispatchers.role-admin") },
    { id: "tl", name: t("dispatchers.role-team-lead") },
    { id: "bk", name: t("dispatchers.role-booking") },
    { id: "hr", name: t("dispatchers.role-hr") },
    { id: "op", name: t("dispatchers.role-operation") },
    { id: "ac", name: t("dispatchers.role-accounting") },
  ];

  const params = ref({ page: 1, pageSize: 10 });
  const pagination = { itemsPerPage: [10, 25, 50, 100] };

  const getPreviewPath = (file: any) => `/api/v1/files/${file.previewPath}`;
  const generateId = () => customAlphabet("1234567890abcdef", 16)();
  const hasStandardTimezone = (value: string) => {
    const [, tz] = value.split(" ");
    return ["EDT", "EST"].includes(tz);
  };
  const getAssetUrl = (path: string) => `https://hugohunter.fra1.digitaloceanspaces.com/${path}`;

  const getSearchParams = (key: string) => {
    const raw = localStorage.getItem(`${key}-params`);
    return raw ? JSON.parse(raw) : {};
  };
  const setSearchParams = (key: string, p: Record<string, any>) =>
    localStorage.setItem(`${key}-params`, JSON.stringify(p));

  const hasAccess = (section: NavigationItem, action: RoleAction = RoleAction.view) => {
    if (!appStore.dispatcher?.roles) return false;
    const sectionRoles = (Permissions[section] || {})[action] || [];
    if (sectionRoles === undefined) {
      console.error(`No action ${action} in permissions section ${section}`);
    }
    return appStore.dispatcher.roles.some((r) => sectionRoles.includes(r));
  };

  const hasRole = (role: Role | Role[]) => {
    if (typeof role === "string") role = [role];
    return role.some((r) => appStore.dispatcher?.roles.includes(r));
  };

  const clearDigits = (value: string) => value.replace(/[^.\d]/g, "");
  const normalizePhone = (value: string) => clearDigits(value);

  const setTitle = (title: string, title2: string | null = null, showInHeader = false) => {
    appStore.setTitle(title);
    if (title2) title = `${title} – ${title2}`;
    if (showInHeader) {
      appStore.setHeaderTitle(title);
    } else {
      appStore.setHeaderTitle("");
    }

    const account = appStore.dispatcher?.accountName ? ` – ${appStore.dispatcher.accountName}` : "";
    document.title = `${title}${account} – TMS`;
  };

  const showChat = (user: any) => chatStore.setChatUser(user);
  const transformUpperCase = (e: any) => (e.target.value = e.target.value.toUpperCase());
  const transformLowerCase = (e: any) => (e.target.value = e.target.value.toLowerCase());

  const debounce = (func: Function, timeout = 300) => {
    let timer: number;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = window.setTimeout(() => func(...args), timeout);
    };
  };

  const scrollToError = () => {
    document.querySelector(".v-field--error:first-of-type")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const uniqueArray = (objects: any[], uniqueBy: string[], keepFirst = true) => {
    return Array.from(
      objects
        .reduce((map, e) => {
          const key = uniqueBy
            .map((k) => [e[k], typeof e[k]])
            .flat()
            .join("-");
          if (keepFirst && map.has(key)) return map;
          return map.set(key, e);
        }, new Map())
        .values(),
    );
  };

  return {
    avatarColor,
    roles,
    params,
    pagination,
    getPreviewPath,
    generateId,
    hasStandardTimezone,
    getAssetUrl,
    getSearchParams,
    setSearchParams,
    hasAccess,
    hasRole,
    clearDigits,
    normalizePhone,
    setTitle,
    showChat,
    transformUpperCase,
    transformLowerCase,
    debounce,
    scrollToError,
    uniqueArray,
  };
}
